import { getActivePinia } from 'pinia';
import { createRouter, createWebHashHistory } from 'vue-router';
import { usePrefillParams, useUrlParams } from '@/composables/useUrlParams.js';
import { useRouteExceptions } from '@/composables/useRouteExceptions.js';

import { useDataLayer } from '@/composables/useDataLayer.js';
import { formStore } from '@/stores/form.js';

import defaultRoutes from './routes.default';
import { pakketStore } from "@/stores/pakket.js";

const router = createRouter({
  history: createWebHashHistory(),
  routes: defaultRoutes,
  scrollBehavior() {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Scroll to the funnel element
        const el = document.querySelector('#funnel');
        if (el) {
          const yOffset = -100; // 100px above the element for header
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          resolve({ left: 0, top: y });
        } else {
          resolve({ left: 0, top: 0 });
        }
      }, 500);
    });
  },
});

const bootstrapRouter = async () => {
  const useFormStore = formStore();
  const { addDataLayer } = useDataLayer();

  // Reset store if there are prefill params are set or the 'funnelType' variable is different than the one in the form store
  // This means the user started a new funnel from the landing page with prefill params
  const { hasPrefillParams } = usePrefillParams();
  const { hasUrlParams } = useUrlParams();

  // eslint-disable-next-line no-undef
  if (hasUrlParams.value || hasPrefillParams.value || funnelType !== useFormStore.funnelType) {
    await clearStore();
  } else {
    useFormStore.setStartedWithPrefills(false);
  }

  await import(`./funnels/routes.${useFormStore.funnelType}.js`).then((module) => {
    const funnelRoutes = module.default.routes;

    // Add the funnel routes.
    funnelRoutes.forEach((route) => {
      if (route.path.indexOf(useFormStore.funnelType)) {
        router.addRoute(route);
      }
    });

    // add a guard for this funnel
    router.beforeEach((to, from, next) => {
      const { updateRoutesForFunnel } = useRouteExceptions();

      if (from.name === 'bedankt') {
        const funnelEntryTemp = useFormStore.funnelEntry;
        const funnelTypeTemp = useFormStore.funnelType;
        clearStore().then(async () => {
          await useFormStore.setFunnelType(funnelTypeTemp);
          await useFormStore.setFunnelEntry(funnelEntryTemp);

          // todo: zou zonder functie moeten kunnen
          window.open(getUrl(), '_SELF');
        });
      }

      // Dynamically load the correct route exceptions based on the active funnel type
      updateRoutesForFunnel();

      const currentRouteIndex = funnelRoutes.findIndex((route) => route.name === to.name);
      useFormStore.page = to.name;
      useFormStore.stepKey = to.meta.stepKey;
      const namedRoute = module.default.validateRouterStep(currentRouteIndex + 1);

      if (namedRoute && to.name !== namedRoute) {
        next({ name: namedRoute });
      } else {
        next();
      }
    });

    router.afterEach(async (to) => {
      const routeIndex = funnelRoutes.findIndex((i) => i.name === to.name);
      const pakket = pakketStore();

      // Thankyou page for insuretostudy needs init information for datalayer
      if (to.name === 'bedankt' && pakket.module === 'insuretostudy') {
        await useFormStore.initializeState();
      }

      addDataLayer(to.meta.stepKey === 'start-calculation' ? 'begin_checkout' : 'checkout', routeIndex + 1);
      useFormStore.hideLoaders();
    });
  });
  return router;
};

const clearStore = async () => {
  await sessionStorage.clear();
  const activeStores = Object.keys(getActivePinia().state.value);
  activeStores.forEach((store) => {
    getActivePinia()._s.get(store).$reset();
  });
};

const getUrl = (queryVars = {}) => {
  let url = window.location.href;
  const params = new URLSearchParams(queryVars);
  url = url.split('?')[0];
  url = url.split('#')[0];
  return `${url}?${params.toString()}`;
};

export default bootstrapRouter;
